import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getCurrentAdminCompany, getCurrentCompanyId } from 'src/modules/companies/slices';
import { RootState } from 'src/store';
import { debounce } from 'ts-debounce';
import { useCampaignService, useCampaignsService } from '../services';
import { useCampaignTypeSelectionDialogController } from './CampaignTypeSelectionDialogController';

export const useCampaignsViewController = () => {
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const company = useSelector(getCurrentAdminCompany);
  const {
    loading: campaignsLoading,
    filter: campaignsFilter,
    list: campaignList,
    getCampaigns,
    changeCampaignsFilter,
    resetCampaigns,
  } = useCampaignsService();
  const { activateCampaign: activate, deactivateCampaign: deactivate } = useCampaignService();

  const campaignTypeSelectionDialogController = useCampaignTypeSelectionDialogController();

  const initLoad = useCallback(async () => {
    try {
      await getCampaigns(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, []);

  const reloadCampaigns = useCallback(async () => {
    try {
      await getCampaigns(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, []);

  const reloadCampaignsDebounce = useMemo(() => debounce(reloadCampaigns, 500), []);

  const changeCampaignFilterForm = useCallback(async (filter: Partial<RootState["campaigns"]["filter"]>) => {
    changeCampaignsFilter(filter);
    await reloadCampaigns();
  }, []);

  const changeCampaignsSearchFilter = useCallback(async (_: ChangeEvent<HTMLInputElement>, query: string) => {
    changeCampaignsFilter({ query });
    await reloadCampaignsDebounce();
  }, []);

  const changeCampaignsSorting = useCallback(async (s: GridSortModel) => {
    changeCampaignsFilter({ sort: `${s[0].field}_${s[0].sort}` });
    await reloadCampaigns();
  }, []);

  const changeCampaignsPagination = useCallback(async (p: GridPaginationModel) => {
    changeCampaignsFilter({ page: p.page, limit: p.pageSize, offset: p.page * p.pageSize });
    await reloadCampaigns();
  }, []);

  const activateCampaign = useCallback(async (campaignId: string) => {
    try {
      await activate(currentCompanyId, [campaignId]);
      await getCampaigns(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, []);

  const deactivateCampaign = useCallback(async (campaignId: string) => {
    try {
      await deactivate(currentCompanyId, [campaignId]);
      await getCampaigns(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, []);

  useEffect(() => {
    initLoad();

    return () => {
      resetCampaigns();
    };
  }, []);

  return {
    isPageLoading: campaignsLoading.getList,
    campaignsFilter,
    campaignList,
    company,
    changeCampaignFilterForm,
    changeCampaignsSearchFilter,
    changeCampaignsSorting,
    changeCampaignsPagination,
    activateCampaign,
    deactivateCampaign,
    campaignTypeSelectionDialogController,
  };
};
